import { useEffect, useId, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { asciiScanField } from '@/pages/fulfillment/components/asciiScanField';
import { CameraScanField } from '@/pages/fulfillment/components/CameraScanField';
import { isComposingKey } from '@/pages/fulfillment/components/MentionSuggest';
import { LockIcon, PencilIcon } from '@/pages/fulfillment/components/UltimateIcons';
import {
  LOCATION_CODE_ERROR,
  UNASSIGNED_SHELF,
  isLocationCode,
  isUnassignedShelf,
  normalizeLocationCode,
  shelfLabel,
} from '../inventoryView';
import type { InventoryScreenRow } from '../inventoryView';

export interface LocationCellProps {
  row: InventoryScreenRow;
  /** The floor is counting: the code reads as text and no move can start from this cell. */
  locked: boolean;
  open: boolean;
  /** Another save holds the screen (an audit opening), so the ✎ waits. */
  busy?: boolean;
  onOpen: () => void;
  onClose: () => void;
  /**
   * Settles once the move has been answered. Resolves true when the server never answered and the move may still have
   * landed: the box then only retries the same move or closes, and closing re-reads the shelf [SSD-7527].
   */
  onMove: (toLocation: string, units: number) => Promise<boolean>;
}

/**
 * The units a save would move: the whole shelf, or, when some units were asked for, that count — nothing when it is
 * not 1 to all-but-one, since the whole shelf has its own path and a count of all moves the reservations too [SSD-7527].
 */
export const unitsToMove = (row: InventoryScreenRow, partial: boolean, draft: string): number | undefined => {
  if (!partial) {
    return row.onHand;
  }
  const text = draft.trim();
  const units = /^\d+$/.test(text) ? Number(text) : NaN;
  return Number.isInteger(units) && units >= 1 && units < row.onHand ? units : undefined;
};

export const unitsError = (row: InventoryScreenRow): string =>
  `Enter 1 to ${row.onHand - 1} units — the whole shelf moves without this box.`;

/**
 * Said before a partial save when the shelf would keep fewer units than it has reserved: the server refuses that
 * move, so the desk hears it first. The screen's counts may be stale, so the server's answer is the last word.
 */
export const moveWarning = (row: InventoryScreenRow, units: number): string | null => {
  const stay = row.onHand - units;
  return units < row.onHand && stay < row.reserved
    ? `Only ${stay} would stay for ${row.reserved} reserved — the server will refuse this move (as the screen reads now).`
    : null;
};

/**
 * Where the box would send the shelf: `null` when nothing would move (the To box is empty, or names the shelf the row
 * is already on), `undefined` when the code is not one. Parking as Unassigned is its own link, never an emptied box,
 * so the To box can start empty [SSD-7565].
 */
export const moveTarget = (row: InventoryScreenRow, draft: string, park = false): string | null | undefined => {
  if (park) {
    // The whole shelf leaves its shelf and waits as Unassigned, still on hand and reservable [SSD-7213, D9].
    return isUnassignedShelf(row.locationCode) ? null : UNASSIGNED_SHELF;
  }
  const next = normalizeLocationCode(draft);
  if (!next || next === row.locationCode) {
    return null;
  }
  return isLocationCode(next) ? next : undefined;
};

/**
 * What a save would do, said before it is made: the whole shelf moves, and its reservations with it [SSD-7446];
 * some of its units move and the reservations stay on the shelf [SSD-7527].
 */
export const moveSummary = (row: InventoryScreenRow, target: string | null, count: number = row.onHand): string => {
  if (target === null) {
    return 'Same shelf — nothing moves.';
  }
  if (count < row.onHand) {
    const from = shelfLabel(row.locationCode);
    const some = `${count} of ${row.onHand} units`;
    const stay =
      row.reserved > 0
        ? ` ${row.reserved} reserved ${row.reserved === 1 ? 'unit stays' : 'units stay'} on ${from}.`
        : '';
    return (
      (isUnassignedShelf(target)
        ? `${some} leave ${from} and wait as Unassigned, still on hand and reservable.`
        : `Moves ${some} from ${from} to ${target}.`) + stay
    );
  }
  const one = row.onHand === 1;
  const units = one ? 'the 1 unit' : `all ${row.onHand} units`;
  const reserved =
    row.reserved > 0
      ? ` ${row.reserved} reserved ${row.reserved === 1 ? 'unit moves' : 'units move'} with the shelf.`
      : '';
  const from = shelfLabel(row.locationCode);
  if (isUnassignedShelf(target)) {
    const subject = units.charAt(0).toUpperCase() + units.slice(1);
    return `${subject} ${one ? 'leaves' : 'leave'} ${from} and wait${one ? 's' : ''} as Unassigned, still on hand and reservable.${reserved}`;
  }
  return `Moves ${units} from ${from} to ${target}.${reserved}`;
};

const LOCKED_REASON = 'Locked while the stock audit counts this screen: confirm or exit the audit to move the shelf.';

interface LocationBoxProps {
  row: InventoryScreenRow;
  busy: boolean;
  onClose: () => void;
  onMove: (toLocation: string, units: number) => Promise<boolean>;
}

/** Some units cannot be parked: only the whole shelf parks as Unassigned [SSD-7213], from its own link [SSD-7565]. */
export const PARTIAL_UNASSIGNED_ERROR = 'Some units cannot wait as Unassigned — only the whole shelf parks there.';
/** The To box starts empty, so the first thing the box asks for is where the stock goes [SSD-7565]. */
export const EMPTY_TO_HINT = 'Scan or type the shelf the stock goes to.';
export const PARK_LINK = 'Park the whole shelf as Unassigned';
export const UNPARK_LINK = 'Move to a shelf instead';
/** The box after a request the server never answered: the move may have landed, so nothing in it is edited. */
export const RETRY_ONLY_HINT =
  'The server never answered — ✓ sends the same move again; ✕ closes and re-reads the shelf.';

/**
 * The box a ✎ opens: From (the shelf the server holds, as text) → To (an empty box). It mounts fresh on every
 * opening, so nothing an earlier, cancelled opening typed survives. The whole shelf moves unless "Move some units
 * only" is opened, which asks for a count [SSD-7527]; parking the whole shelf as Unassigned is its own link [SSD-7565].
 */
const LocationBox = ({ row, busy, onClose, onMove }: LocationBoxProps) => {
  const [draft, setDraft] = useState('');
  const [park, setPark] = useState(false);
  const [partial, setPartial] = useState(false);
  const [retryOnly, setRetryOnly] = useState(false);
  const [unitsDraft, setUnitsDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // Enter and a ✓ click can both land before `saving` re-renders, so the guard is a ref.
  const savingRef = useRef(false);
  const boxRef = useRef<HTMLSpanElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const unitsRef = useRef<HTMLInputElement>(null);
  const unitsId = useId();
  const dismissRef = useRef({ saving, onClose });
  dismissRef.current = { saving, onClose };

  useEffect(() => {
    // The box overhangs its row, so bring the whole box into view rather than only the input the focus would chase.
    inputRef.current?.focus({ preventScroll: true });
    boxRef.current?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  }, []);

  useEffect(() => {
    // A click anywhere else on the page cancels; the cell counts as inside, since its ✎ already toggles the box.
    // Switching browser tabs fires no pointer event, so the box keeps what was typed. The camera dialog is a
    // portal outside the cell, and a tap in it is part of filling the box, not leaving it.
    const dismissOutside = (event: PointerEvent) => {
      const anchor = boxRef.current?.parentElement ?? boxRef.current;
      const target = event.target as Node | null;
      const inCamera = target instanceof Element && target.closest('.modal, .modal-backdrop') !== null;
      if (!dismissRef.current.saving && anchor && target && !anchor.contains(target) && !inCamera) {
        dismissRef.current.onClose();
      }
    };
    document.addEventListener('pointerdown', dismissOutside);
    return () => document.removeEventListener('pointerdown', dismissOutside);
  }, []);

  useEffect(() => {
    // The count is the next thing to type once some units were asked for — after the To shelf, if it is still empty.
    if (partial) {
      const toFilled = normalizeLocationCode(inputRef.current?.value ?? '') !== '';
      (toFilled ? unitsRef : inputRef).current?.focus({ preventScroll: true });
    }
  }, [partial]);

  const target = moveTarget(row, draft, park);
  const empty = !park && normalizeLocationCode(draft) === '';
  const units = unitsToMove(row, partial, unitsDraft);
  const warning = units !== undefined && target !== null && target !== undefined ? moveWarning(row, units) : null;

  const save = async () => {
    // An audit opening holds the screen; a move now would land on a shelf it is about to count.
    if (savingRef.current || busy) {
      return;
    }
    if (empty) {
      inputRef.current?.focus({ preventScroll: true });
      return;
    }
    if (target === undefined) {
      setError(LOCATION_CODE_ERROR);
      inputRef.current?.focus({ preventScroll: true });
      return;
    }
    if (units === undefined) {
      setError(unitsError(row));
      unitsRef.current?.focus({ preventScroll: true });
      return;
    }
    if (target !== null && units < row.onHand && isUnassignedShelf(target)) {
      setError(PARTIAL_UNASSIGNED_ERROR);
      inputRef.current?.focus({ preventScroll: true });
      return;
    }
    if (target === null) {
      onClose();
      return;
    }
    savingRef.current = true;
    setSaving(true);
    try {
      if (await onMove(target, units)) {
        setRetryOnly(true);
      }
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    // A key pressed while an IME is still composing belongs to the composition: it neither saves nor cancels.
    if (isComposingKey(event)) {
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      void save();
      return;
    }
    if (event.key === 'Escape' && !saving) {
      event.preventDefault();
      onClose();
      return;
    }
    // Tab leaves the box, and leaving the box never moves stock.
    if (event.key === 'Tab' && !saving) {
      onClose();
    }
  };

  const shelfBox = (
    <input
      ref={inputRef}
      className={error === LOCATION_CODE_ERROR ? 'loc-in bad' : 'loc-in'}
      aria-label={`New location ${row.sku}`}
      aria-invalid={error === LOCATION_CODE_ERROR}
      placeholder={park ? 'Unassigned' : 'Scan or type new shelf'}
      maxLength={32}
      value={draft}
      disabled={saving || busy || retryOnly}
      // A desk scanner types the shelf label like a keyboard, so with the OS keyboard on Korean it arrives as
      // Hangul and its Enter is swallowed; this box reads it back as the label and still saves on that Enter,
      // as the retired Stock Move desk's From/To boxes did [SSD-7527, SSD-7050].
      {...asciiScanField((value) => {
        setDraft(value);
        setPark(false);
        setError(null);
      }, onKeyDown)}
    />
  );

  return (
    <span className="tot-pop loc-pop" ref={boxRef} role="group" aria-label={`Move ${row.sku} to another shelf`}>
      <span className="loc-from">
        From <b>{shelfLabel(row.locationCode)}</b> → To
      </span>
      <span className="prow">
        {/* On a phone the shelf is read off its label; the read fills the box and ✓ or Enter is still the desk's [SSD-7527]. */}
        <CameraScanField
          label={`Scan new location ${row.sku} with camera`}
          className="loc-cam"
          ultimate
          enterAfterRead={false}
          disabled={saving || busy || retryOnly}
        >
          {shelfBox}
        </CameraScanField>
        <button
          type="button"
          className="tot-ok"
          aria-label={`Move location ${row.sku}`}
          disabled={saving || busy || empty}
          onClick={() => void save()}
        >
          ✓
        </button>
        <button
          type="button"
          className="tot-no"
          aria-label={`Cancel location change ${row.sku}`}
          disabled={saving}
          onClick={onClose}
        >
          ✕
        </button>
      </span>
      {/* One unit cannot be split, and a count of all is the whole shelf's own path, so the toggle needs two or more. */}
      {row.onHand > 1 && (
        <button
          type="button"
          className="loc-part"
          aria-pressed={partial}
          disabled={saving || retryOnly}
          onClick={() => {
            setPartial((was) => !was);
            setPark(false);
            setUnitsDraft('');
            setError(null);
          }}
        >
          {partial ? 'Move the whole shelf instead' : 'Move some units only'}
        </button>
      )}
      {/* Only the whole shelf parks, and a shelf already Unassigned has nowhere to park [SSD-7213, SSD-7565]. */}
      {!partial && !isUnassignedShelf(row.locationCode) && (
        <button
          type="button"
          className="loc-part"
          aria-pressed={park}
          disabled={saving || retryOnly}
          onClick={() => {
            setPark((was) => !was);
            setDraft('');
            setError(null);
            inputRef.current?.focus({ preventScroll: true });
          }}
        >
          {park ? UNPARK_LINK : PARK_LINK}
        </button>
      )}
      {partial && (
        <span className="prow lrow">
          <label className="loc-lbl" htmlFor={unitsId}>
            Units
          </label>
          <input
            id={unitsId}
            ref={unitsRef}
            type="text"
            inputMode="numeric"
            className={error !== null && error !== LOCATION_CODE_ERROR ? 'tot-in bad' : 'tot-in'}
            aria-label={`Units to move ${row.sku}`}
            aria-invalid={error !== null && error !== LOCATION_CODE_ERROR}
            placeholder={`1–${row.onHand - 1}`}
            maxLength={6}
            value={unitsDraft}
            disabled={saving || busy || retryOnly}
            onChange={(event) => {
              setUnitsDraft(event.target.value);
              setError(null);
            }}
            onKeyDown={onKeyDown}
          />
          <span className="loc-of">of {row.onHand}</span>
        </span>
      )}
      {error ? (
        <span className="tot-bad" role="alert">
          {error}
        </span>
      ) : (
        <span className="tot-hint loc-summary" aria-live="polite">
          {target === undefined
            ? LOCATION_CODE_ERROR
            : empty
              ? EMPTY_TO_HINT
              : units === undefined
                ? unitsError(row)
                : target !== null && units < row.onHand && isUnassignedShelf(target)
                  ? PARTIAL_UNASSIGNED_ERROR
                  : moveSummary(row, target, units)}
          {warning && <span className="tot-warn">{warning}</span>}
          {retryOnly && <span className="tot-warn">{RETRY_ONLY_HINT}</span>}
        </span>
      )}
    </span>
  );
};

/**
 * The Location column. The code reads as text and moves only from the box its ✎ opens, on Enter or ✓:
 * leaving the box cancels, because a half-typed code is still a valid shelf and the whole shelf would follow it
 * [SSD-7456]. While the floor counts, the code is locked text, so nothing on the row looks typeable.
 */
export const LocationCell = ({ row, locked, open, busy = false, onOpen, onClose, onMove }: LocationCellProps) => {
  const lockedReasonId = useId();
  const label = shelfLabel(row.locationCode);

  if (locked) {
    return (
      <span
        className="loc-v locked"
        aria-label={`Location ${row.sku}`}
        aria-describedby={lockedReasonId}
        title={LOCKED_REASON}
      >
        <LockIcon />
        <span className="loc-code">{label}</span>
        <span id={lockedReasonId} className="visually-hidden">
          {LOCKED_REASON}
        </span>
      </span>
    );
  }

  return (
    <span className="loc-cell">
      <span
        className={isUnassignedShelf(row.locationCode) ? 'loc-v unassigned' : 'loc-v'}
        aria-label={`Location ${row.sku}`}
        title={label}
      >
        {label}
      </span>
      <button
        type="button"
        className="tot-edit loc-edit"
        aria-label={`Edit location ${row.sku}`}
        aria-expanded={open}
        disabled={busy}
        onClick={() => (open ? onClose() : onOpen())}
      >
        <PencilIcon />
      </button>
      {open && <LocationBox row={row} busy={busy} onClose={onClose} onMove={onMove} />}
    </span>
  );
};
